import React, { createContext, useEffect, useState, useCallback } from "react";

import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { StorageKeys } from "src/platform/constants/Storage";
import { IUser } from "src/platform/types/shared-types";
import debugLogger from "src/platform/utils/debugLogger";

import authService, { LoginCredentials } from "../services/authService";
import { orgSessionManager } from "../services/orgSessionManager";
import { clearOrganizationData } from "../utils/organizationHelpers";

interface UserContextType {
  user: IUser | null;
  setUser: (user: IUser) => void;
  login: (credentials: LoginCredentials) => Promise<void>;
  signUp: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loginWithOkta: (authState: any) => void;
  processOAuthToken: (token: string) => void;
  logout: () => void;
  loading: boolean;
  error: string | null;
  isOktaAuthenticated: boolean;
  authInProgress: boolean;
  setAuthInProgress: (status: boolean) => void;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  login: async () => {},
  signUp: async () => {},
  loginWithOkta: () => {},
  processOAuthToken: () => {},
  logout: () => {},
  loading: false,
  error: null,
  isOktaAuthenticated: false,
  authInProgress: false,
  setAuthInProgress: () => {},
});

/**
 * User Provider Component
 * Manages authentication state using authService.
 *
 * The session JWT lives in an httpOnly cookie set by the API. The UI never
 * touches the token directly. On startup, GET /auth/me validates the cookie
 * and restores the user + org context.
 */
export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOktaAuthenticated, setIsOktaAuthenticated] = useState(false);
  const [authInProgress, setAuthInProgress] = useState(false);

  // Initialize user from authService and validate the session via /auth/me
  useEffect(() => {
    const init = async () => {
      const { user: storedUser } = authService.initializeAuth();

      // Validate the httpOnly cookie session and restore org context
      try {
        const response = await fetch(`${apiBaseUrls.BACKEND}/api/v1/auth/me`, {
          credentials: "include",
        });

        if (response.ok) {
          const data = (await response.json()) as {
            user?: IUser;
            orgAlias?: string;
            orgId?: number;
          };
          if (data.user) {
            setUser(data.user);
            localStorage.setItem(StorageKeys.USER, JSON.stringify(data.user));
          } else if (storedUser) {
            setUser(storedUser);
          }
          if (data.orgAlias) {
            orgSessionManager.onSessionRestored(
              data.orgAlias,
              data.orgId ?? null,
            );
          }
        } else if (response.status === 401) {
          // No session is the normal unauthenticated bootstrap state. Let the
          // public route render the login page instead of reloading it.
          orgSessionManager.clearSession();
          localStorage.removeItem(StorageKeys.USER);
        }
      } catch {
        // Network error — fall back to stored user if available
        if (storedUser) {
          setUser(storedUser);
        }
      }

      const oktaAuthStatus = localStorage.getItem(StorageKeys.OKTA_AUTH);
      if (oktaAuthStatus) {
        setIsOktaAuthenticated(true);
      }

      setLoading(false);
    };

    void init();
  }, []);

  // Handle user update through authService
  const handleSetUser = useCallback((newUser: IUser) => {
    setUser(newUser);
    localStorage.setItem(StorageKeys.USER, JSON.stringify(newUser));
  }, []);

  // Login with credentials using authService
  const login = useCallback(async (credentials: LoginCredentials) => {
    setAuthInProgress(true);
    setError(null);
    try {
      const result = await authService.login(credentials);
      if (result.user) {
        setUser(result.user);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      setError(errorMessage);
      debugLogger.error("Login error:", err);
      throw err;
    } finally {
      setAuthInProgress(false);
    }
  }, []);

  // Sign up with user data
  const signUp = useCallback(
    async (userData: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    }) => {
      setAuthInProgress(true);
      setError(null);
      try {
        const result = await authService.signup(userData);
        if (result.user) {
          setUser(result.user);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Sign up failed";
        setError(errorMessage);
        throw err;
      } finally {
        setAuthInProgress(false);
      }
    },
    [],
  );

  // Process Okta auth state using authService
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loginWithOkta = useCallback((authState: any) => {
    if (authState.isAuthenticated) {
      try {
        const result = authService.processOktaAuth(authState);
        if (result.user) {
          setUser(result.user);
        }
        setIsOktaAuthenticated(true);
      } catch (err) {
        debugLogger.error("Okta auth processing error:", err);
      }
    }
  }, []);

  // Process OAuth token using authService
  const processOAuthToken = useCallback((tokenValue: string) => {
    try {
      authService.processOAuthToken(tokenValue);
      // User data may be loaded later or separately via /auth/me
    } catch (err) {
      debugLogger.error("OAuth token processing error:", err);
    }
  }, []);

  // Logout using authService
  const logout = useCallback(() => {
    void authService.logout();
    clearOrganizationData();
    setUser(null);
    setError(null);
    setIsOktaAuthenticated(false);
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: handleSetUser,
        login,
        signUp,
        loginWithOkta,
        processOAuthToken,
        logout,
        loading,
        error,
        isOktaAuthenticated,
        authInProgress,
        setAuthInProgress,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
